/**
 * Lazy TF.js loader that forces the WebGL backend before anything else
 * touches `tf`. The pose-detection package transitively bundles
 * tfjs-backend-webgpu, which TF.js auto-registers as the highest-
 * priority backend; without an explicit `setBackend('webgl')` the
 * other call sites get the "webgpu has not yet been initialized"
 * warning and the first inference hangs.
 *
 * One bottleneck for backend selection means every trainer + predict
 * path stays consistent — no surprise webgpu paths on a Chromebook
 * that may or may not have working webgpu drivers.
 */

type Tf = typeof import('@tensorflow/tfjs');

let tfPromise: Promise<Tf> | null = null;

export async function getTf(): Promise<Tf> {
  if (!tfPromise) {
    tfPromise = (async () => {
      const tf = await import('@tensorflow/tfjs');
      // setBackend resolves once the backend is initialised. We try
      // webgl first (mature on every Chromebook we care about); if
      // it's somehow unavailable the kid still gets a usable CPU
      // fallback rather than a wedged training run.
      try {
        await tf.setBackend('webgl');
      } catch {
        try {
          await tf.setBackend('cpu');
        } catch {
          // No backend available — let TF.js error out at use-time
          // with its own message rather than swallowing here.
        }
      }
      await tf.ready();
      return tf;
    })().catch((err) => {
      tfPromise = null;
      throw err;
    });
  }
  return tfPromise;
}
